<template>
  <div>
    <transition name="fade" appear>
      <main class="login">
        <section class="login__wrapper">
          <div class="login__left">
            <img class="login__image" alt="login image" src="@/assets/images/login/login_page.png" />
          </div>
          <div class="login__right">
            <nav>
              <a href="/">
                <img class="login__logo" src="@/assets/images/brand/logo_full.svg" alt="Early Stage Online" />
              </a>
            </nav>
            <section class="login__form-wrapper">
              <div class="login__form">
                <div class="login__form-heading">
                  <h1 class="text-heading-m text-color-grey-900">Zaloguj się</h1>
                  <p class="text-body-large text-color-grey-700">do świata Early Stage</p>
                </div>
                <form @submit.prevent="doLogin" class="w-full">
                  <div class="w-full login__form-content">
                    <BaseInput class="w-full" type="text" inputmode="email" v-model="email" autocomplete="email"
                      :applyErrorStyle="isError" :noMargin="true">
                      Adres email
                    </BaseInput>
                    <PasswordInput class="w-full" type="password" name="password" passwordEye v-model="password"
                      autocomplete="current-password" :applyErrorStyle="isError" :noMargin="true">
                      Hasło
                    </PasswordInput>
                    <div v-if="isError" class="login__form-error">
                      <span class="error-wrapper text-color-error-700">
                        <img class="information-icon" src="@/assets/images/icons/i_error.svg" alt="error" />
                        {{ errorText }}
                      </span>
                    </div>
                    <div class="login__form-description">
                      <p class="text-body-medium text-color-grey-700">*Jeśli nie aktywowałeś jeszcze swojego konta
                        skorzystaj z opcji</p>
                      <router-link :to="{ name: 'Auth.ForgotPassword' }" class="esc-link">Nie pamiętam
                        hasła</router-link>
                    </div>
                    <button type="submit" class="esc-button">Zaloguj się</button>
                  </div>
                </form>
                <div class="login__form-additional">
                  <p class="text-body-medium text-color-grey-900">Nie jesteś jeszcze klientem Early Stage?</p>
                  <a class="esc-link" href="https://earlystage.pl/pl/nasze-szkoly" target="_blank">Zapisz się</a>
                </div>
              </div>
            </section>
            <footer class="login__footer">
              <a class="esc-link-tonal" :href="REGULATIONS_PATH" target="_blank">Regulamin</a>
              <a class="esc-link-tonal" href="/Polityka_Prywatności_ES_Online.pdf" target="_blank">Polityka
                prywatności</a>
            </footer>
          </div>
        </section>
      </main>
    </transition>
  </div>
</template>

<script>
import {
  mapState, mapMutations, mapGetters, mapActions,
} from 'vuex';
import PasswordInput from '@/components/Form/BasePasswordInput.vue';
import BaseInput from '@/components/Form/BaseInput.vue';

export default {
  name: 'LoginNew',
  components: {
    PasswordInput,
    BaseInput,
  },

  data() {
    return {
      email: null,
      password: null,
      isError: false,
      errorText: '',
      isOnline: navigator.onLine,
      hasAccount: false,
      showBackOnline: false,
      isDisable: false,
    };
  },

  computed: {
    ...mapState('students', ['students']),
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['isOnlyAgentRole', 'isAdditionalAccessProfile', 'getAgentData', 'getAdditionalAccessData']),
    userRoles() {
      return this.user.roles;
    },
  },

  methods: {
    ...mapActions('auth', ['login', 'clearStore', 'clearAuth']),
    ...mapActions('materials', ['loadAllMaterials']),
    ...mapMutations('auth', ['setCurrentProfile', 'setAccess']),
    ...mapMutations('materials', ['setRealizations']),

    async doLogin() {
      this.isDisable = true;
      this.$store.commit('layout/startLoading');

      try {
        this.loginError = false;

        await this.login({
          email: this.email,
          password: this.password,
        });

        await this.loadAllMaterials();

        if (Object.prototype.hasOwnProperty.call(this.$route.params, 'next')) {
          this.routerPush({ name: this.$route.params.next });
        } else {
          const agentId = this.getAgentData?.id;
          const additionalAccessId = this.getAdditionalAccessData?.id;
          const userId = this.user?.id;
          const calculatedUserId = (this.isOnlyAgentRole && agentId ? agentId : userId) || additionalAccessId;
          this.gtmPush({
            eventName: 'Poprawne zalogowanie użytkownika', eventPage: 'Poprawne zalogowanie użytkownika', eventValue: { childrens: `Ilość dzieci tego użytkownika: ${this.students?.length}`, user_id: calculatedUserId },
          });

          const idFromUrl = this.$route?.query?.id;

          if (idFromUrl) {
            const selectedStudent = this.students.find(student => String(student.id) === idFromUrl);

            if (!selectedStudent) {
              this.routerPush({ name: 'Auth.Welcome' });
            } else {
              this.setCurrentProfile('ROLE_PARENT');
              this.setRealizations(selectedStudent.applications);
              this.setAccess(selectedStudent.access);
              this.$store.commit('students/setStudent', selectedStudent);
              this.routerPush(this.$route.query.redirect);
            }
          } else {
            this.routerPush({ name: 'Auth.Welcome' });
          }
        }
      } catch (e) {
        this.clearStore();
        this.clearAuth();
        this.isError = true;

        if (e?.response?.data) {
          const errData = e.response.data;

          if (errData?.results?.hadAccount) {
            this.routerPush({
              name: 'Auth.NewPasswordRequest',
              params: { initialEmail: this.email },
            });
          } else if (errData?.errors) {
            this.errorText = e.response.data.errors.fields.password;
          }
        }
      } finally {
        this.isDisable = false;
        this.$store.commit('layout/finishLoading');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.login {
  padding: 24px;
  padding-top: 40px;
  min-height: 100dvh;
  display: flex;

  @media screen and (min-width: 768px) {
    padding: 40px;
  }

  @media screen and (min-width: 1024px) {
    padding: 24px;
  }

  &__wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;

    @media screen and (min-width: 768px) {}

    @media screen and (min-width: 1024px) {
      display: grid;
      align-items: center;
      grid-template-columns: 1fr 1fr;
      gap: 40px;
    }
  }

  &__left {
    border-radius: 24px;
    background: #e8f4ff;
    display: none;
    padding: 24px;
    justify-content: center;
    align-items: center;
    height: 100%;

    @media screen and (min-width: 768px) {}

    @media screen and (min-width: 1024px) {
      display: flex;
    }
  }

  &__right {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 24px;
    height: 100%;
    width: 100%;
  }

  &__image {
    max-width: 420px;
    max-height: 420px;
    aspect-ratio: 1;
    transition: 0.3s;

    @media screen and (min-width: 1279px) {
      max-width: 610px;
      max-height: 610px;
    }
  }

  &__form-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    flex: 1 0 0;
    align-self: stretch;

    @media screen and (min-width: 768px) {
      min-height: 480px;
    }
  }

  &__form {
    display: flex;
    width: 100%;
    max-width: 480px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 24px;

    &-content {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    &-heading {
      display: flex;
      gap: 8px;
      flex-direction: column;
    }

    &-description {
      display: flex;
      gap: 8px;
      flex-direction: column;
      align-items: flex-end;
      text-align: end;

      @media screen and (min-width: 768px) {}

      @media screen and (min-width: 1024px) {
        text-align: start;
      }
    }

    &-error {
      display: flex;
      align-items: center;
      margin-top: -16px;
    }

    &-additional {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      align-self: center;
      gap: 12px;

      @media screen and (min-width: 768px) {
        flex-direction: row;
      }
    }
  }

  &__logo {
    width: 140px;
    height: 38px;
  }

  &__footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 24px;

    @media screen and (min-width: 768px) {}

    @media screen and (min-width: 1024px) {
      display: flex;
    }
  }
}
</style>
